import { dispatchExportInProgressEvent } from "../../components/ServerSideExport/ServerSideExportCommon/ServerSideExportUtil/ServerSideExportEvents";
import type { ExportDetails, ExportFields, TextOverride } from "../../services/ExportV2/exportV2API.types";
import {
  STATUS_IN_PROGRESS,
  STATUS_PENDING,
  EXPORT_FIELD_ID_BY_KPI,
  BASE_EXPORT_FIELD_IDS,
} from "../constants/export-constants";
import { store } from "../../store";
import { storedLocale } from "../../components/Authentication/util";
// Dispatches ExportInProgress event, which is used to hide or show export InProgress icon
export const updateInProgressIndicator = (exportsList: ExportDetails[] = []) => {
  const inProgressStatus = [STATUS_PENDING, STATUS_IN_PROGRESS];
  const isInProgress = exportsList.some(({ ExportStatus }) => inProgressStatus.includes(ExportStatus));
  dispatchExportInProgressEvent(isInProgress);
};

/**
 * Checks if export should be disabled based on session SSO origin or current business config
 */
export const isExportDisabledBySessionSsoOrigin = (): { los: boolean; cml: boolean } => {
  const state = store.getState();
  const { boot, loginLocalStorage } = state;
  const { ssoOrigin } = loginLocalStorage?.loginFields || {};

  // If session SSO origin is TW or CN, disable both exports
  if (ssoOrigin === "TW" || ssoOrigin === "CN") {
    return { los: true, cml: true };
  }

  if (boot.isImpersonation === true && boot.configuration?.disableImpersonationExport === true) {
    return { los: true, cml: true };
  }

  // Otherwise check current business config
  const config = boot.configuration;
  return {
    los: config?.los?.disableExport || false,
    cml: config?.cml?.disableExport || false,
  };
};

/**
 * Translates an array of field IDs into ExportFields with localized titles.
 */
export const getTranslatedFields = (
  fieldIds: string[],
  exportFields: Record<string, string>,
  fieldTitleOverrides?: Record<string, string>,
): ExportFields[] => {
  return fieldIds.map((id) => ({
    id,
    title: fieldTitleOverrides && fieldTitleOverrides[id] ? fieldTitleOverrides[id] : exportFields[id] || id,
  }));
};

/**
 * Maps selected KPI keys to export fields with optional title overrides.
 * Priority: fieldTitleOverrides > exportFields (i18n) > raw fieldId
 */
export const mapKpisToExportFields = (
  selectedKpis: string[],
  exportFields: Record<string, string>,
  fieldTitleOverrides?: Record<string, string>,
): ExportFields[] => {
  const modifiedSelectedKpis = selectedKpis.reduce<ExportFields[]>((acc, kpi) => {
    const fieldId = EXPORT_FIELD_ID_BY_KPI[kpi];

    if (fieldId) {
      acc.push({
        id: fieldId,
        title: fieldTitleOverrides?.[fieldId] || exportFields[fieldId] || fieldId,
      });
    }
    return acc;
  }, []);

  return modifiedSelectedKpis;
};

/**
 * Builds a fieldTitleOverrides map from UI table column labels.
 * Maps each KPI key → export field ID → UI label, so export uses the same
 * labels the user sees in the table.
 */
export const buildFieldTitleOverrides = (tableColumns: Record<string, string>): Record<string, string> => {
  const overrides: Record<string, string> = {};
  for (const [kpiKey, label] of Object.entries(tableColumns)) {
    const exportFieldId = EXPORT_FIELD_ID_BY_KPI[kpiKey];
    if (exportFieldId) {
      overrides[exportFieldId] = label;
    }
  }
  return overrides;
};

/**
 * Builds the complete export fields array (base fields + KPI fields) from
 * translated export field labels, selected KPIs, and optional title overrides.
 *
 * This is the single source of truth for constructing export field lists —
 * used by both ActionReportHeader.triggerExport() and ActionReportExportModal.getExportPayload().
 */
export const buildExportFields = (
  selectedKpis: string[],
  exportFields: Record<string, string>,
  fieldTitleOverrides?: Record<string, string>,
): ExportFields[] => {
  const baseFields = getTranslatedFields(BASE_EXPORT_FIELD_IDS, exportFields, fieldTitleOverrides);
  const kpiFields = mapKpisToExportFields(selectedKpis, exportFields, fieldTitleOverrides);
  return [...baseFields, ...kpiFields];
};

export type BaseExportPayloadParams = {
  moduleId: string;
  reportAffAbo: string;
  period: string;
  startedBy: string;
  countryCode: string;
  addressPattern: string;
  addressLineDivider: string;
  selectedKpis: string[];
  textOverride: TextOverride;
  exportFields: Record<string, string>;
  fieldTitleOverrides?: Record<string, string>;
  filteredByKPIS?: boolean;
};

/**
 * Builds the common portion of an export request payload shared across all
 * action report export flows (toolbar, modal, SAQ custom export).
 *
 * Consumers only need to spread this result and add endpoint-specific fields
 * (e.g. `affAbos`).
 */
export const buildBaseExportPayload = (params: BaseExportPayloadParams) => {
  const {
    moduleId,
    reportAffAbo,
    period,
    startedBy,
    countryCode,
    addressPattern,
    addressLineDivider,
    selectedKpis,
    textOverride,
    exportFields,
    fieldTitleOverrides,
    filteredByKPIS = true,
  } = params;

  const locale = storedLocale();
  const fields = buildExportFields(selectedKpis, exportFields, fieldTitleOverrides);

  return {
    moduleId,
    reportAffAbo,
    period,
    startedBy,
    locale,
    textOverride,
    fields,
    countryCode,
    format: {
      address: {
        pattern: addressPattern,
        addressLineDivider,
      },
    },
    filteredByKPIS,
  };
};
